import { MeasurementTypeI18N, MeasurementUnit } from '@the-real-insight/tri-model';

import { deviceAlarmsOf } from './alertDeviceAlarms';
import { selectionFromRule } from './alertScopeSelection';
import { correctedMeasurementLabel } from './measurementLabelFix';

const OPERATOR_SYMBOLS: Record<string, string> = {
    equal: '=',
    less: '<',
    lessEqual: '≤',
    greater: '>',
    greaterEqual: '≥',
};

/** Joiner words, and optionally plain operator words ("unter") instead of symbols ("<"). */
export interface SummaryWords {
    and: string;
    or: string;
    operators?: Record<string, string>;
}

const I18N: any = MeasurementTypeI18N;
const UNITS: any = MeasurementUnit;

const labelOf = (field: string, language: string): string =>
    correctedMeasurementLabel((I18N[language] ?? I18N.de)?.[field] ?? field);

/**
 * The unit a measurement is stored and compared in. Device readings are always metric (°C, bar, m, …) whatever the
 * UI language is, so this deliberately reads the German table: tri-model's English table lists °F, ft and even
 * "m/s²" for the wind speed, which would show a threshold of 60 °C as "above 60 °F".
 */
const unitOf = (field: string): string => UNITS.de?.[field] ?? '';

/**
 * One condition in words: "Druck < 10 bar", groups as "(A und B)" / "(A oder B)".
 *
 * Plain JSON as well as model instances; an incomplete comparison (no field yet) yields `''` so a
 * half-built rule never prints "undefined".
 */
export const summarizeCondition = (
    condition: any,
    language: string,
    words: SummaryWords,
): string => {
    if (!condition) {
        return '';
    }

    if (Array.isArray(condition.conditions)) {
        const parts = condition.conditions
            .map((child: any) => summarizeCondition(child, language, words))
            .filter((part: string) => part !== '');
        const joiner = condition.conditionType === 'OrCondition' ? words.or : words.and;

        return parts.length > 1 ? `(${parts.join(` ${joiner} `)})` : parts.join('');
    }

    if (!condition.field) {
        return '';
    }

    const symbol = words.operators?.[condition.operator] ?? OPERATOR_SYMBOLS[condition.operator] ?? '';

    if (condition.comparisonType === 'field') {
        return `${labelOf(condition.field, language)} ${symbol} ${labelOf(condition.comparisonField ?? '', language)}`.trim();
    }

    const unit = unitOf(condition.field);
    const value = condition.comparisonValue ?? '…';

    return `${labelOf(condition.field, language)} ${symbol} ${value}${unit ? ` ${unit}` : ''}`.trim();
};

/** The comparisons of the rule in words, joined with "und" — what the list shows as the rule. */
export const summarizeConditions = (
    conditions: any[] | undefined,
    language: string,
    words: SummaryWords,
): string => {
    // "Any of these device alarms" reads as the alarms, not as "> 0" per alarm.
    const alarms = deviceAlarmsOf(conditions);

    if (alarms) {
        return alarms.map((alarm) => labelOf(alarm, language)).join(` ${words.or} `);
    }

    return (conditions ?? [])
        .map((condition) => summarizeCondition(condition, language, words))
        .filter((part) => part !== '')
        .join(` ${words.and} `);
};

/**
 * How many recipients one action names: every address of a mail, one number of a SMS, and for a push
 * action every chosen role and every chosen person (a role is one entry, however many members it has).
 */
export const recipientCount = (actions: any[] | undefined): number =>
    (actions ?? []).reduce((sum: number, action: any) => {
        if (Array.isArray(action?.recipientRoles) || Array.isArray(action?.recipientUsers)) {
            return sum + (action.recipientRoles ?? []).length + (action.recipientUsers ?? []).length;
        }

        if (typeof action?.to === 'string') {
            return sum + action.to.split(',').filter((address: string) => address.trim() !== '').length;
        }

        return sum + (typeof action?.phoneNumber === 'string' && action.phoneNumber.trim() !== '' ? 1 : 0);
    }, 0);

export interface ScopeCounts {
    /** `null` = all (the empty selection), else the number of listed ids. */
    projects: number | null;
    products: number | null;
}

/** Which part of the fleet a rule is limited to — `null` per axis means "no limit". */
export const scopeCountsOf = (rule: any): ScopeCounts => {
    const projects = selectionFromRule(rule?.projects).length;
    const products = selectionFromRule(rule?.products).length;

    return { projects: projects > 0 ? projects : null, products: products > 0 ? products : null };
};
